// V2 Pipeline board — COACH flow (people pipeline). Board-split Phase 1:
// this page only renders leads where flow === 'coach'.
//
//   Prospect → Invited → Connected → Sent → Booked
//
// One full-width board, no tabs. Two-tab shell + Queued + Contacted + Clicked
// collapsed into this simpler flow.
//
// Legacy stages auto-remap in laneOf() on read — no destructive migration:
//   queued / qualified / contacted → invited
//   clicked / hot                  → sent
//   warm / curious / nurturing / article_opened / replying → connected
//
// Behaviours per spec 13b:
//   - Invite (one step): copy message + open LinkedIn + advance card
//   - Manual acceptance: user drags Invited → Connected (no auto)
//   - Invited search: type-ahead over the Invited lane
//   - Article pills on Sent tiles — every send appends one; recipient click
//     lights a grey ✓ bubble, a qualifying read lights a green ✓ bubble
//     (and sorts the tile to the top of Sent)
//   - Top-up cards pinned on Prospect (add more) + Connected (create engagement)
//
// The AddProspectTile (paste-a-LinkedIn-URL) is preserved on the Prospect lane
// as a secondary affordance per Sunny's 2026-08-14 ask.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActionIcon,
  Avatar,
  Button,
  Box,
  Center,
  Group,
  Indicator,
  Loader,
  NumberInput,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowRight,
  IconArrowUpRight,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandWhatsapp,
  IconCircleCheck,
  IconDeviceMobile,
  IconGripVertical,
  IconLink,
  IconMail,
  IconMessage,
  IconPlus,
  IconSearch,
  IconSettings,
  IconSpeakerphone,
  IconWorld,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import useApi from '../hooks/useApi';
import { DndContext, PointerSensor, closestCorners, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  addProspectByUrl,
  fetchArticles,
  fetchCampaigns,
  getConnectedAutoStatus,
  fetchLeads,
  fetchMyPrincipal,
  pingConnectedAutoNextTask,
  postConnectedAutoConsent,
  resetConnectedAutoFailures,
  fetchProspectAutoStatus,
  updateLead,
  updateMyPrincipal,
} from '../services/api';
import { isOrganicCampaign, isOrganicLead } from '../utils/organic';
import {
  buildMailtoUrl,
  buildSmsUrl,
  buildWhatsappUrl,
  normaliseAuPhone,
  pickBestMessage,
} from '../lib/messageLinks';

// ─── Lane taxonomy ───────────────────────────────────────────────────────────
const LANES = ['prospect', 'invited', 'connected', 'sent', 'booked'];
const LANE_TITLES = {
  prospect: 'Prospect',
  invited: 'Invited',
  connected: 'Connected',
  sent: 'Sent',
  booked: 'Booked',
};

const LANE_AUTOMATION_DEFAULTS = {
  prospect: 'manual',
  invited: 'manual',
  connected: 'manual',
  sent: 'manual',
};

const LOCKED_LANES = new Set();

// stage → lane. Order matters: terminal + explicit stages checked first.
function laneOf(lead) {
  const stage = String(lead.stage || 'prospect').toLowerCase();
  if (stage === 'booked' || stage === 'closed') return 'booked';
  if (stage === 'sent') return 'sent';
  if (stage === 'clicked' || stage === 'hot') return 'sent';
  if (stage === 'connected') return 'connected';
  if (stage === 'warm' || stage === 'curious' || stage === 'nurturing'
    || stage === 'article_opened' || stage === 'replying') return 'connected';
  if (stage === 'invited') return 'invited';
  if (stage === 'queued' || stage === 'qualified' || stage === 'contacted') return 'invited';
  return 'prospect';
}

function normalizeLaneAutomation(raw) {
  const normalized = { ...LANE_AUTOMATION_DEFAULTS };
  if (!raw || typeof raw !== 'object') return normalized;
  for (const lane of Object.keys(normalized)) {
    const value = String(raw[lane] || '').toLowerCase();
    if (lane === 'prospect' || lane === 'connected') {
      if (value === 'auto' || value === 'manual' || value === 'semi') normalized[lane] = value;
      continue;
    }
    if (value === 'auto' || value === 'manual') normalized[lane] = value;
  }
  return normalized;
}

// Drag-target → patch payload.
function laneToPatch(targetLane) {
  const nowIso = new Date().toISOString();
  if (targetLane === 'prospect') return { stage: 'prospect', approvedAt: null, approvedMessage: null };
  if (targetLane === 'invited')  return { stage: 'invited', invitedAt: nowIso, lastTouchAt: nowIso };
  if (targetLane === 'connected') return { stage: 'connected', lastTouchAt: nowIso };
  if (targetLane === 'sent') return { stage: 'sent', lastTouchAt: nowIso, lastActivityAt: nowIso };
  if (targetLane === 'booked') return { stage: 'booked', bookedAt: nowIso, lastActivityAt: nowIso };
  return null;
}

function hasSentArticleShareDraft(lead) {
  return Array.isArray(lead?.articleDrafts) && lead.articleDrafts.some((d) =>
    d?.source === 'article-share' && d?.approvedAt
  );
}

// ─── Small helpers ───────────────────────────────────────────────────────────
function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fitColorFor(score) {
  const v = Number(score || 0);
  if (v >= 80) return 'var(--mantine-color-sage-6)';
  if (v >= 60) return 'var(--mantine-color-sage-4)';
  return 'var(--aviary-muted)';
}

function finiteSortOrder(value) {
  return (typeof value === 'number' && Number.isFinite(value)) ? value : null;
}

function computeProspectDropSortOrder(orderedProspects, movedId, currentSortOrder) {
  const index = orderedProspects.findIndex((lead) => String(lead.id) === String(movedId));
  if (index < 0) return currentSortOrder;
  const prev = index > 0 ? orderedProspects[index - 1] : null;
  const next = index < (orderedProspects.length - 1) ? orderedProspects[index + 1] : null;
  const prevOrder = finiteSortOrder(prev?.sortOrder);
  const nextOrder = finiteSortOrder(next?.sortOrder);

  if (!prev && !next) return currentSortOrder;
  if (!prev) {
    if (nextOrder != null) return nextOrder - 1000;
    const current = finiteSortOrder(currentSortOrder);
    return (current != null ? current : 0) - 1000;
  }
  if (!next) {
    if (prevOrder != null) return prevOrder + 1000;
    const current = finiteSortOrder(currentSortOrder);
    return (current != null ? current : 1000) + 1000;
  }
  if (prevOrder != null && nextOrder != null) return (prevOrder + nextOrder) / 2;
  if (prevOrder != null) return prevOrder + 1000;
  if (nextOrder != null) return nextOrder - 1000;
  return currentSortOrder;
}

function dayKeyForTimezone(dateLike, timezone) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(dateLike));
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(dateLike));
  }
}

function shortWeekdayForTimezone(dateLike, timezone) {
  try {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: timezone,
      weekday: 'short',
    }).format(new Date(dateLike));
  } catch {
    return new Intl.DateTimeFormat('en-AU', { weekday: 'short' }).format(new Date(dateLike));
  }
}

function formatCountdownClock(dateLike, timezone) {
  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  let value = '';
  try {
    value = new Intl.DateTimeFormat('en-US', { ...options, timeZone: timezone }).format(new Date(dateLike));
  } catch {
    value = new Intl.DateTimeFormat('en-US', options).format(new Date(dateLike));
  }
  return value.toLowerCase().replace(':00', '').replace(/\s+/g, '');
}

function formatNextSendCountdown(nextSendAt, nowMs, timezone) {
  if (!nextSendAt) return null;
  const targetTs = Date.parse(nextSendAt);
  if (Number.isNaN(targetTs)) return null;
  const diff = targetTs - nowMs;
  if (diff < 60000) return 'any moment';
  if (diff < (60 * 60000)) return `sends in ~${Math.round(diff / 60000)} min`;

  const now = new Date(nowMs);
  const target = new Date(targetTs);
  const nowKey = dayKeyForTimezone(now, timezone);
  const targetKey = dayKeyForTimezone(target, timezone);
  if (nowKey === targetKey) return `sends in ~${Math.round(diff / 3600000)}h`;

  const tomorrowKey = dayKeyForTimezone(new Date(nowMs + (24 * 60 * 60 * 1000)), timezone);
  if (targetKey === tomorrowKey) return `tomorrow ~${formatCountdownClock(target, timezone)}`;

  return `${shortWeekdayForTimezone(target, timezone)} ~${formatCountdownClock(target, timezone)}`;
}

function formatAge(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff) || diff < 0) return '';
  const days = Math.floor(diff / 86400000);
  const hrs  = Math.floor(diff / 3600000);
  const mins = Math.floor(diff / 60000);
  if (days > 0) return `${days}d`;
  if (hrs > 0) return `${hrs}h`;
  if (mins > 0) return `${mins}m`;
  return 'now';
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function normalizeUrl(raw, { hostPrefix = '' } = {}) {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  if (hostPrefix) {
    if (value.startsWith('@')) return `${hostPrefix}/${value.slice(1)}`;
    if (!value.includes('.') && !value.includes('/')) return `${hostPrefix}/${value}`;
  }
  return `https://${value.replace(/^\/+/, '')}`;
}

function extensionGateLabel(reason) {
  if (!reason) return 'paused';
  if (reason === 'outside-business-hours') return 'outside business hours';
  if (reason === 'daily-cap-reached') return 'daily cap reached';
  if (reason === 'circuit-paused') return 'circuit breaker';
  if (reason === 'consent-stale') return 'consent required';
  return reason;
}

function ExtensionStatusPill({ status }) {
  const connected = Boolean(status?.extensionConnected);
  const install = Array.isArray(status?.extensionInstalls) && status.extensionInstalls[0];
  const version = install?.extensionVersion || null;
  const label = connected ? 'Connected' : 'Connect';
  const bg = connected ? 'var(--mantine-color-sage-0)' : '#FFF5F5';
  const border = connected ? 'var(--mantine-color-sage-4)' : '#F3B0B0';
  const fg = connected ? 'var(--mantine-color-sage-8)' : '#9C2A2A';
  const dot = connected ? 'var(--mantine-color-sage-6)' : '#C63A3A';
  return (
    <Tooltip
      label={connected
        ? `Chrome extension${version ? ` v${version}` : ''} connected`
        : 'Chrome extension not detected — install & sign in from the popup.'}
      withArrow
    >
      <Group
        gap={6}
        align="center"
        style={{
          padding: '4px 10px',
          borderRadius: 999,
          border: `1px solid ${border}`,
          background: bg,
          color: fg,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dot,
            display: 'inline-block',
            boxShadow: connected ? `0 0 0 0 ${dot}` : 'none',
            animation: connected ? 'aviary-pulse 1.6s ease-out infinite' : 'none',
          }}
        />
        {label}
      </Group>
    </Tooltip>
  );
}

// CSS keyframes for the pulse — injected once. Using a global style tag
// avoids pulling in a whole styles file for this one animation.
if (typeof document !== 'undefined' && !document.getElementById('aviary-pulse-style')) {
  const s = document.createElement('style');
  s.id = 'aviary-pulse-style';
  s.textContent = `@keyframes aviary-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(90, 143, 122, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(90, 143, 122, 0); }
    100% { box-shadow: 0 0 0 0 rgba(90, 143, 122, 0); }
  }`;
  document.head.appendChild(s);
}

function FitChip({ score }) {
  const v = Math.round(Number(score || 0));
  if (!v || v < 10) return null;
  return (
    <Box style={{
      width: 32, height: 22, borderRadius: 12,
      border: `1.5px solid ${fitColorFor(v)}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Text size="xs" fw={600} style={{ color: 'var(--aviary-ink)', lineHeight: 1 }}>{v}</Text>
    </Box>
  );
}

function fallbackLabelForSource(source) {
  if (source === 'campaign-touch') return 'Touch';
  return 'Article';
}

function MagicLinkPill({ item }) {
  const read = Boolean(item?.readAt);
  const clicked = Boolean(item?.clickedAt);
  const sent = Boolean(item?.sentAt);
  const failed = Boolean(item?.failedAt) && !sent;
  const forwards = Number(item?.forwardedReaderCount || 0);
  // Visual hierarchy (strongest signal wins):
  //  read    → green fill + bold green tick (scroll ≥ 80% OR dwell ≥ 45s)
  //  clicked → yellow text + yellow tick (opened but didn't engage)
  //  failed  → red text + warning border (extension attempted, failed to send)
  //  else    → muted gray (queued for send, no activity yet)
  const pillBackground = read
    ? 'var(--mantine-color-sage-1)'
    : '#fff';
  const pillBorderColor = read
    ? 'var(--mantine-color-sage-6)'
    : (failed ? 'var(--mantine-color-red-4)' : 'var(--aviary-disabled)');
  const textColor = read
    ? 'var(--mantine-color-sage-9)'
    : (clicked
      ? 'var(--mantine-color-yellow-8)'
      : (failed ? 'var(--mantine-color-red-7)' : 'var(--aviary-muted)'));
  const textWeight = read ? 600 : 400;
  const iconColor = read
    ? 'var(--mantine-color-sage-7)'
    : (clicked
      ? 'var(--mantine-color-yellow-7)'
      : (failed ? 'var(--mantine-color-red-6)' : 'var(--aviary-muted)'));
  const checkSize = read ? 14 : 12;
  const rawLabel = String(item?.destinationLabel || '').trim();
  const fallbackLabel = fallbackLabelForSource(String(item?.source || '').trim());
  const label = rawLabel || fallbackLabel;
  const parts = [];
  if (sent) parts.push(`sent ${formatAge(item.sentAt)} ago`);
  if (clicked) parts.push(`clicked ${formatAge(item.clickedAt)} ago`);
  if (read) parts.push(`read ${formatAge(item.readAt)} ago`);
  if (failed) parts.push(`send failed ${formatAge(item.failedAt)} ago — retry from lead detail`);
  if (forwards > 0) parts.push(`${forwards} forwards`);
  const tooltip = parts.length > 0 ? `${label} — ${parts.join(' · ')}` : label;
  const href = String(item?.destinationUrl || '').trim();
  const chip = (
    <UnstyledButton
      component="a"
      href={href || undefined}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'block' }}
    >
      <Paper
        radius="sm"
        p={4}
        withBorder
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          maxWidth: 260,
          background: pillBackground,
          borderColor: pillBorderColor,
        }}
      >
        <IconLink size={12} style={{ color: iconColor, flexShrink: 0 }} />
        <Text
          size="xs"
          truncate
          style={{
            color: textColor,
            fontWeight: textWeight,
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
            fontStyle: rawLabel ? 'normal' : 'italic',
          }}
        >
          {label}
        </Text>
        {(read || clicked) && (
          <IconCircleCheck
            size={checkSize}
            style={{
              color: read ? 'var(--mantine-color-sage-7)' : 'var(--mantine-color-yellow-7)',
              flexShrink: 0,
            }}
          />
        )}
      </Paper>
    </UnstyledButton>
  );

  return (
    <Tooltip label={tooltip} withArrow>
      <Box style={{ display: 'inline-flex' }}>
        <Indicator
          size={7}
          color="orange"
          offset={2}
          position="top-end"
          withBorder
          disabled={!(forwards > 0)}
        >
          {chip}
        </Indicator>
      </Box>
    </Tooltip>
  );
}

// ─── LeadCard — three-line tile ──────────────────────────────────────────────
function LeadCard({
  lead, lane, articles, onClick, onDragStart, onDragEnd, isDragging,
  nativeDraggable = true,
  dndRef = null,
  prospectHandle = null,
  nextSendCountdown = null,
  dndStyle = null,
  dndIsDragging = false,
}) {
  const isBooked = lane === 'booked';
  const [isHovering, setIsHovering] = useState(false);
  const iconColor = isBooked ? 'rgba(255,255,255,0.85)' : 'var(--aviary-muted)';

  const channelLinks = useMemo(() => {
    const channels = [];
    const message = pickBestMessage(lead);
    const email = firstNonEmpty(lead.email, lead.contactEnrichment?.emails?.[0]);
    if (email) {
      channels.push({
        key: 'email',
        href: buildMailtoUrl(lead, message),
        title: email,
        icon: IconMail,
      });
    }

    const phoneRaw = firstNonEmpty(lead.phone, lead.contactEnrichment?.mobiles?.[0]);
    const phoneKind = String(lead.phoneKind || lead.contactEnrichment?.phoneKind || '').toLowerCase();
    if (phoneRaw) {
      const canMessagePhone = phoneKind === 'mobile' || !phoneKind;
      if (canMessagePhone) {
        const waPhone = normaliseAuPhone(phoneRaw);
        const whatsappHref = buildWhatsappUrl(lead, message);
        const smsHref = buildSmsUrl(lead, message);
        if (whatsappHref) {
          channels.push({
            key: 'whatsapp',
            href: whatsappHref,
            title: waPhone ? `wa.me/${waPhone}` : phoneRaw,
            icon: IconBrandWhatsapp,
          });
        }
        if (smsHref) {
          channels.push({
            key: 'sms',
            href: smsHref,
            title: waPhone ? `sms:${waPhone}` : phoneRaw,
            icon: IconMessage,
          });
        }
      }
      if (phoneKind === 'landline') {
        const telPhone = normaliseAuPhone(phoneRaw) || phoneRaw;
        channels.push({
          key: 'phone',
          href: `tel:${telPhone}`,
          title: phoneRaw,
          icon: IconDeviceMobile,
        });
      }
    }

    const instagram = normalizeUrl(lead.contactEnrichment?.socials?.instagram, { hostPrefix: 'https://instagram.com' });
    if (instagram) {
      channels.push({
        key: 'instagram',
        href: instagram,
        title: lead.contactEnrichment?.socials?.instagram || instagram,
        icon: IconBrandInstagram,
      });
    }
    const facebook = normalizeUrl(lead.contactEnrichment?.socials?.facebook, { hostPrefix: 'https://facebook.com' });
    if (facebook) {
      channels.push({
        key: 'facebook',
        href: facebook,
        title: lead.contactEnrichment?.socials?.facebook || facebook,
        icon: IconBrandFacebook,
      });
    }
    const linkedin = normalizeUrl(firstNonEmpty(lead.linkedinUrl, lead.contactEnrichment?.socials?.linkedin), {
      hostPrefix: 'https://linkedin.com/in',
    });
    if (linkedin) {
      channels.push({
        key: 'linkedin',
        href: linkedin,
        title: firstNonEmpty(lead.linkedinUrl, lead.contactEnrichment?.socials?.linkedin, linkedin),
        icon: IconBrandLinkedin,
      });
    }
    const website = normalizeUrl(firstNonEmpty(lead.website, lead.contactEnrichment?.website));
    if (website) {
      channels.push({
        key: 'website',
        href: website,
        title: firstNonEmpty(lead.website, lead.contactEnrichment?.website, website),
        icon: IconWorld,
      });
    }

    return channels;
  }, [
    lead.email,
    lead.phone,
    lead.phoneKind,
    lead.linkedinUrl,
    lead.website,
    lead.contactEnrichment,
    lead.approvedMessage,
    lead.draftMessages,
  ]);

  const approvedDrafts = useMemo(() => {
    const drafts = Array.isArray(lead.articleDrafts) ? lead.articleDrafts : [];
    return drafts.filter((d) => d?.approvedAt);
  }, [lead.articleDrafts]);
  const sentArticleSlugs = useMemo(() => new Set(
    approvedDrafts
      .filter((d) => d.source === 'article-share')
      .map((d) => d.articleSlug)
      .filter(Boolean)
  ), [approvedDrafts]);
  const maxApprovedAt = useMemo(() => (
    approvedDrafts.reduce((max, d) => {
      const ts = d.approvedAt ? new Date(d.approvedAt).getTime() : 0;
      return ts > max ? ts : max;
    }, 0)
  ), [approvedDrafts]);
  const hasFresherArticle = useMemo(() => (
    (articles || []).some((a) => {
      if (!a.slug || sentArticleSlugs.has(a.slug)) return false;
      const pub = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      return pub > maxApprovedAt;
    })
  ), [articles, sentArticleSlugs, maxApprovedAt]);
  const isCooled = sentArticleSlugs.size > 0;
  const isCooling = isCooled && !hasFresherArticle;
  const isReadyForNext = isCooled && hasFresherArticle;
  const orderedMagicLinks = useMemo(() => {
    const rawItems = Array.isArray(lead.magicLinks) ? [...lead.magicLinks] : [];
    const drafts = Array.isArray(lead.articleDrafts) ? lead.articleDrafts : [];
    // Bug ref (2026-09-21): skipped intro-message drafts leave a stale magic
    // link on the lead — pill kept showing "Intro message" after the coach
    // skipped it. Filter those out. Simultaneously, sent article-share drafts
    // sometimes lack a magic link entry (race in v2PipelineShare.js /send flow)
    // — synthesize display entries for those so the pill correctly shows the
    // article title. Backend fix tracked separately.
    const draftByLinkId = new Map();
    for (const d of drafts) {
      if (!d?.linkId) continue;
      draftByLinkId.set(String(d.linkId), d);
    }
    const items = rawItems.filter((item) => {
      const draft = draftByLinkId.get(String(item?.linkId || ''));
      if (!draft) return true;
      const status = String(draft.status || '').toLowerCase();
      return status !== 'skipped';
    });
    const knownLinkIds = new Set(items.map((item) => String(item?.linkId || '')));
    for (const d of drafts) {
      const linkId = String(d?.linkId || '');
      if (!linkId || knownLinkIds.has(linkId)) continue;
      const status = String(d.status || '').toLowerCase();
      // Show synthetic pill for sent/approved (queued for extension) AND failed
      // (Sunny reported 2026-09-22: failed drafts were invisible on board — he
      // couldn't tell which prospects had a broken article-share he needed to
      // retry). Skip 'skipped' (coach opted out) and any other transient states.
      if (status !== 'sent' && status !== 'approved' && status !== 'failed') continue;
      items.push({
        linkId,
        source: d.source || 'article-share',
        destinationLabel: String(d.articleTitle || '').trim(),
        destinationUrl: d.linkUrl || '',
        articleSlug: d.articleSlug || null,
        sentAt: d.sentAt || null,
        clickedAt: d.clickedAt || null,
        readAt: d.readAt || null,
        failedAt: status === 'failed' ? (d.failedAt || null) : null,
        lastActivityAt: d.failedAt || d.clickedAt || d.sentAt || d.approvedAt || d.createdAt || null,
      });
    }
    // Dedupe unsent duplicates on the same destinationUrl — keep the most
    // recent entry per URL. Historical bug (pre-v2.22.1) minted a fresh
    // linkId on every lead-detail visit, polluting the array. Sent entries
    // pass through untouched so activity history stays visible.
    const byKey = new Map();
    for (const item of items) {
      if (item?.sentAt) {
        byKey.set(`sent:${item.linkId}`, item);
        continue;
      }
      const url = String(item?.destinationUrl || '').trim();
      const dedupeKey = url ? `unsent:${url}` : `unsent:${item?.linkId || Math.random()}`;
      const prior = byKey.get(dedupeKey);
      if (!prior) {
        byKey.set(dedupeKey, item);
        continue;
      }
      const priorTs = new Date(prior?.lastActivityAt || 0).getTime();
      const nextTs = new Date(item?.lastActivityAt || 0).getTime();
      if (nextTs > priorTs) byKey.set(dedupeKey, item);
    }
    return [...byKey.values()].sort((a, b) => {
      const ta = new Date(a?.lastActivityAt || a?.readAt || a?.clickedAt || a?.sentAt || 0).getTime();
      const tb = new Date(b?.lastActivityAt || b?.readAt || b?.clickedAt || b?.sentAt || 0).getTime();
      return tb - ta;
    });
  }, [lead.magicLinks, lead.articleDrafts]);
  const visibleMagicLinks = orderedMagicLinks.slice(0, 3);
  const hiddenMagicLinkCount = Math.max(0, orderedMagicLinks.length - visibleMagicLinks.length);

  const cardStyle = isBooked
    ? { background: 'var(--mantine-color-sage-7)', color: '#fff', border: '1px solid var(--mantine-color-sage-7)' }
    : isCooling
      ? { background: 'var(--mantine-color-gray-1)', color: 'var(--aviary-muted)', border: '1px solid var(--mantine-color-gray-3)', opacity: 0.75 }
      : isReadyForNext
        ? { background: '#fff', color: 'var(--aviary-ink)', border: '1px solid var(--mantine-color-sage-3)' }
        : { background: '#fff', color: 'var(--aviary-ink)', border: '1px solid var(--mantine-color-sage-3)' };

  return (
    <Paper
      ref={dndRef}
      p="sm"
      radius="md"
      draggable={nativeDraggable}
      onDragStart={(e) => {
        if (!nativeDraggable) return;
        e.dataTransfer.setData('text/plain', lead.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.(lead.id);
      }}
      onDragEnd={() => {
        if (!nativeDraggable) return;
        onDragEnd?.();
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        ...cardStyle,
        ...(dndStyle || {}),
        cursor: (dndIsDragging || isDragging) ? 'grabbing' : (prospectHandle ? 'pointer' : 'grab'),
        opacity: (dndIsDragging || isDragging) ? 0.4 : 1,
        transition: 'opacity 120ms ease, box-shadow 120ms ease',
      }}
    >
      <Stack gap={8}>
        <Group gap={8} wrap="nowrap" align="flex-start">
          {prospectHandle && (
            <Box style={{
              marginTop: 2,
              opacity: isHovering ? 1 : 0.5,
              transition: 'opacity 120ms ease',
              color: 'var(--aviary-muted)',
              flexShrink: 0,
            }}>
              {prospectHandle}
            </Box>
          )}
          <Avatar src={lead.photoUrl || null} size={30} radius="xl" color={isBooked ? 'gray' : 'sage'}>
            {initialsFor(lead.name)}
          </Avatar>
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} style={{
              color: isBooked ? '#fff' : 'var(--aviary-ink)',
              lineHeight: 1.25,
            }} truncate>
              {lead.name || '(no name)'}
            </Text>
            <Text size="xs" style={{
              color: isBooked ? 'rgba(255,255,255,0.75)' : 'var(--aviary-muted)',
              lineHeight: 1.25,
            }} truncate>
              {lead.title || '—'}
            </Text>
            {lead.company && String(lead.company).trim().toLowerCase() !== String(lead.name || '').trim().toLowerCase() && (
              <Text size="xs" style={{
                color: isBooked ? 'rgba(255,255,255,0.65)' : 'var(--aviary-muted)',
                lineHeight: 1.25,
              }} truncate>
                {lead.company}
              </Text>
            )}
          </Stack>
          {!isBooked && <FitChip score={typeof lead.fitScore === 'number' && lead.fitScore >= 10 ? lead.fitScore : null} />}
        </Group>

        {channelLinks.length > 0 && (
          <Group gap={6} wrap="wrap">
            {channelLinks.map((channel) => {
              const Icon = channel.icon;
              return (
                <UnstyledButton
                  key={channel.key}
                  component="a"
                  href={channel.href}
                  target="_blank"
                  rel="noreferrer"
                  title={channel.title}
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Icon size={14} style={{ color: iconColor }} />
                </UnstyledButton>
              );
            })}
          </Group>
        )}

        {visibleMagicLinks.length > 0 && (
          <Stack gap={4}>
            {visibleMagicLinks.map((item) => (
              <MagicLinkPill
                key={item.linkId}
                item={item}
              />
            ))}
            {hiddenMagicLinkCount > 0 && (
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                +{hiddenMagicLinkCount} more
              </Text>
            )}
          </Stack>
        )}

        {nextSendCountdown && (
          <Text size="xs" style={{ color: 'var(--aviary-muted)', lineHeight: 1.25 }}>
            {nextSendCountdown}
          </Text>
        )}

        {lane === 'invited' && (
          <Text size="xs" style={{ color: 'var(--aviary-muted)', lineHeight: 1.25 }}>
            invited {formatAge(lead.invitedAt || lead.lastTouchAt || lead.updatedAt)} ago · accepted? drag →
          </Text>
        )}

        {isBooked && lead.bookedAt && (
          <Text size="xs" fw={500} style={{ color: 'rgba(255,255,255,0.9)' }}>
            workshop · {new Date(lead.bookedAt).toLocaleDateString('en-AU', {
              weekday: 'short', day: 'numeric', month: 'short',
            })}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function SortableProspectCard({
  lead,
  articles,
  onClick,
  onNativeDragStart,
  onNativeDragEnd,
  nextSendCountdown,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(lead.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    boxShadow: isDragging ? '0 10px 24px rgba(0, 0, 0, 0.16)' : 'none',
  };

  const handle = (
    <Box
      {...attributes}
      {...listeners}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      aria-label="Reorder prospect"
    >
      <IconGripVertical size={14} />
    </Box>
  );

  return (
    <LeadCard
      lead={lead}
      lane="prospect"
      articles={articles}
      onClick={onClick}
      onDragStart={onNativeDragStart}
      onDragEnd={onNativeDragEnd}
      isDragging={false}
      nativeDraggable
      dndRef={setNodeRef}
      prospectHandle={handle}
      nextSendCountdown={nextSendCountdown}
      dndStyle={style}
      dndIsDragging={isDragging}
    />
  );
}

// ─── Lane mode caption (Iteration 20: quiet) ────────────────────────────────
// Mode is metadata, not an alert. One-line caption with a small colored dot.
// Green dot = auto. Muted dot = manual. Amber survives ONLY as a small badge
// when a manual lane has actual work waiting (colour means "act", not "setting").
function LaneCaption({ mode, text, workBadge, actionButton }) {
  const dotColor = mode === 'auto' ? 'var(--mantine-color-sage-6)' : 'var(--aviary-disabled)';
  return (
    <Group gap={8} align="center" wrap="nowrap" px={4}>
      <Box style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
      <Text size="xs" style={{ color: 'var(--aviary-muted)', flex: 1 }} truncate>
        {text}
      </Text>
      {workBadge && (
        <Text
          size="xs"
          fw={600}
          style={{
            padding: '1px 8px',
            borderRadius: 999,
            background: 'var(--mantine-color-yellow-1)',
            border: '1px solid var(--mantine-color-yellow-6)',
            color: 'var(--mantine-color-yellow-8)',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {workBadge}
        </Text>
      )}
      {actionButton && (
        <UnstyledButton
          onClick={actionButton.onClick}
          disabled={actionButton.busy}
          style={{
            padding: '2px 10px',
            borderRadius: 999,
            background: 'var(--mantine-color-red-1)',
            border: '1px solid var(--mantine-color-red-6)',
            color: 'var(--mantine-color-red-8)',
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: actionButton.busy ? 'not-allowed' : 'pointer',
            opacity: actionButton.busy ? 0.6 : 1,
          }}
        >
          {actionButton.busy ? 'Resuming…' : actionButton.label}
        </UnstyledButton>
      )}
    </Group>
  );
}

// ─── Invited-lane search ─────────────────────────────────────────────────────
function InvitedSearch({ value, onChange, totalCount, matchedCount }) {
  const hidden = value.trim() ? Math.max(0, totalCount - matchedCount) : 0;
  return (
    <Stack gap={4}>
      <TextInput
        size="xs"
        placeholder="Search invited by name…"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        leftSection={<IconSearch size={12} />}
        styles={{ input: { background: '#fff', border: '1px solid var(--mantine-color-sage-3)' } }}
      />
      {value.trim() && (
        <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic', paddingLeft: 4 }}>
          {matchedCount} of {totalCount} match{hidden > 0 ? ` · ${hidden} hidden` : ''}
        </Text>
      )}
    </Stack>
  );
}

// ─── Top-up cards ────────────────────────────────────────────────────────────
function ProspectTopUpCard({ campaigns, onNew, onRefresh }) {
  const activeCampaigns = (campaigns || []).filter((c) => {
    const s = String(c.status || '').toLowerCase();
    return s !== 'archived' && s !== 'completed' && s !== 'draft';
  });
  const MAX_LIST = 5;
  const visible = activeCampaigns.slice(0, MAX_LIST);
  const overflow = Math.max(0, activeCampaigns.length - visible.length);
  return (
    <Paper p="sm" radius="md" style={{ background: '#FFFCF3', border: '1px dashed #F6C265' }}>
      <Stack gap={6}>
        <Group gap={6} wrap="nowrap" align="center">
          <IconPlus size={13} style={{ color: '#8A5A00' }} />
          <Text size="xs" fw={600} style={{ color: '#8A5A00' }}>Add more prospects</Text>
        </Group>
        <Text size="xs" style={{ color: '#8A5A00', opacity: 0.85 }}>
          Fill this lane when it runs low.
        </Text>
        <UnstyledButton onClick={onNew} style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'left' }}>
          <Text size="xs" fw={500} style={{ color: '#8A5A00' }}>+ New campaign wizard →</Text>
        </UnstyledButton>
        {visible.map((c) => (
          <UnstyledButton
            key={c.id}
            onClick={() => onRefresh(c)}
            style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'left' }}
          >
            <Text size="xs" fw={500} style={{ color: '#8A5A00' }} truncate>
              ↻ Refresh &quot;{c.name || 'Untitled'}&quot; →
            </Text>
          </UnstyledButton>
        ))}
        {overflow > 0 && (
          <Text size="xs" style={{ color: '#8A5A00', opacity: 0.6, fontStyle: 'italic' }}>
            +{overflow} more in Campaigns
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function ConnectedTopUpCard({ connectedCount, articles, onNewArticle, onShareArticle, onShareWithAll }) {
  const published = (articles || []).filter((a) => {
    const status = String(a.articleStatus || '').toLowerCase();
    return status !== 'draft';
  });
  const MAX_LIST = 5;
  const visible = published.slice(0, MAX_LIST);
  const overflow = Math.max(0, published.length - visible.length);
  return (
    <Paper p="sm" radius="md" style={{
      background: 'var(--mantine-color-sage-0)',
      border: '1px dashed var(--mantine-color-sage-4)',
    }}>
      <Stack gap={6}>
        <Group gap={6} wrap="nowrap" align="center">
          <IconSpeakerphone size={13} style={{ color: 'var(--mantine-color-sage-8)' }} />
          <Text size="xs" fw={600} style={{ color: 'var(--mantine-color-sage-8)' }}>Create more engagement</Text>
        </Group>
        <Text size="xs" style={{ color: 'var(--mantine-color-sage-8)', opacity: 0.85 }}>
          Keep the connected pool warm with fresh articles.
        </Text>
        <UnstyledButton onClick={onNewArticle} style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'left' }}>
          <Text size="xs" fw={500} style={{ color: 'var(--mantine-color-sage-8)' }}>
            + New article (co-write · link · upload) →
          </Text>
        </UnstyledButton>
        {visible.map((a) => (
          <UnstyledButton
            key={a.slug || a.id}
            onClick={() => onShareArticle(a)}
            disabled={connectedCount === 0}
            style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'left', opacity: connectedCount === 0 ? 0.4 : 1 }}
          >
            <Text size="xs" fw={500} style={{ color: 'var(--mantine-color-sage-8)' }} truncate>
              📄 Share &quot;{a.title || a.slug || 'Untitled'}&quot; →
            </Text>
          </UnstyledButton>
        ))}
        {overflow > 0 && (
          <Text size="xs" style={{ color: 'var(--mantine-color-sage-8)', opacity: 0.6, fontStyle: 'italic' }}>
            +{overflow} more in Articles
          </Text>
        )}
        {connectedCount > 0 && (
          <UnstyledButton onClick={onShareWithAll} style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'left' }}>
            <Text size="xs" fw={500} style={{ color: 'var(--mantine-color-sage-8)' }}>
              🗣 Share with all {connectedCount} connected →
            </Text>
          </UnstyledButton>
        )}
      </Stack>
    </Paper>
  );
}

// ─── AddProspectTile — paste-a-LinkedIn-URL manual add ───────────────────────
function AddProspectTile({ selectedCampaignId, campaigns, onAdded }) {
  const api = useApi();
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const activeCampaigns = (campaigns || []).filter((c) => {
    const s = String(c.status || '').toLowerCase();
    return s !== 'archived' && s !== 'completed' && !isOrganicCampaign(c);
  });
  const filterCampaign = selectedCampaignId && selectedCampaignId !== 'all'
    ? activeCampaigns.find((c) => String(c.id) === String(selectedCampaignId))
    : null;
  const [pickedCampaignId, setPickedCampaignId] = useState(
    filterCampaign?.id || activeCampaigns[0]?.id || null
  );
  useEffect(() => {
    if (filterCampaign?.id) setPickedCampaignId(filterCampaign.id);
  }, [filterCampaign?.id]);

  const targetCampaign = filterCampaign
    || activeCampaigns.find((c) => String(c.id) === String(pickedCampaignId))
    || activeCampaigns[0];
  const disabled = !targetCampaign;

  async function submit(e) {
    e.preventDefault();
    if (disabled || submitting || !targetCampaign) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { lead } = await addProspectByUrl(api, targetCampaign.id, trimmed);
      notifications.show({
        title: 'Prospect added',
        message: `${lead.name || 'New prospect'} is waiting in ${targetCampaign.name || 'the campaign'}.`,
        color: 'sage',
      });
      setUrl('');
      onAdded?.(lead);
    } catch (err) {
      notifications.show({
        title: 'Could not add prospect',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  const showPicker = !filterCampaign && activeCampaigns.length > 1;
  const hint = disabled
    ? 'create a campaign first'
    : (submitting
        ? 'looking them up…'
        : (filterCampaign ? `adds to ${filterCampaign.name || 'this campaign'}` : `adds to ${targetCampaign?.name || 'campaign'}`));

  return (
    <Paper p="sm" radius="md" style={{
      background: 'transparent',
      border: '1px dashed var(--aviary-disabled)',
      opacity: disabled ? 0.5 : 1,
    }}>
      <form onSubmit={submit}>
        <Stack gap={6}>
          <Group gap={6} align="center">
            <IconBrandLinkedin size={13} style={{ color: 'var(--aviary-muted)' }} />
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)' }}>Add one by LinkedIn URL</Text>
          </Group>
          <TextInput
            size="xs"
            placeholder="paste a LinkedIn URL…"
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit(e);
            }}
            disabled={disabled || submitting}
            rightSectionPointerEvents="all"
            rightSectionWidth={32}
            rightSection={(
              <ActionIcon
                type="submit"
                size="xs"
                color="sage"
                variant="filled"
                loading={submitting}
                disabled={disabled || submitting || !url.trim()}
                aria-label="Add prospect by LinkedIn URL"
              >
                <IconArrowRight size={12} />
              </ActionIcon>
            )}
          />
          {showPicker && (
            <Select
              size="xs"
              value={pickedCampaignId ? String(pickedCampaignId) : null}
              onChange={(v) => setPickedCampaignId(v)}
              data={activeCampaigns.map((c) => ({ value: String(c.id), label: c.name || 'Untitled campaign' }))}
              allowDeselect={false}
              withCheckIcon={false}
              placeholder="pick a campaign"
              styles={{ input: { fontSize: 12 } }}
            />
          )}
          <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>{hint}</Text>
        </Stack>
      </form>
    </Paper>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
const MAX_VISIBLE_PER_LANE = 8;
const DRAG_EDGE_PX = 60;
const DRAG_SCROLL_SPEED = 12;

const BOARD_PATH = '/v2/board/people';
const INCLUDE_NETWORK_STORAGE_PREFIX = 'aviary.includeLinkedinNetwork';

function includeNetworkStorageKey(principalId) {
  return `${INCLUDE_NETWORK_STORAGE_PREFIX}:${String(principalId || 'unknown')}`;
}

export default function AviaryV2BoardCoachPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [articles, setArticles] = useState([]);
  const [principal, setPrincipal] = useState(null);
  const [laneAutomation, setLaneAutomation] = useState(LANE_AUTOMATION_DEFAULTS);
  const [prospectAutoStatus, setProspectAutoStatus] = useState(null);
  const [connectedAutoStatus, setConnectedAutoStatus] = useState(null);
  const [resettingConnectedFailures, setResettingConnectedFailures] = useState(false);
  const [updatingLane, setUpdatingLane] = useState(null);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [includeOrganicNetwork, setIncludeOrganicNetwork] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [invitedSearch, setInvitedSearch] = useState('');
  const [draggingLeadId, setDraggingLeadId] = useState(null);
  const [hoverLane, setHoverLane] = useState(null);
  const [expandedLanes, setExpandedLanes] = useState(() => new Set());
  const [expandedLaneConfig, setExpandedLaneConfig] = useState(null);
  const [savingThrottle, setSavingThrottle] = useState(false);
  const [connectedModeDraft, setConnectedModeDraft] = useState('manual');
  const [connectedCapDraft, setConnectedCapDraft] = useState(10);
  const [savingConnectedSettings, setSavingConnectedSettings] = useState(false);
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now());
  const boardRef = useRef(null);
  const prospectDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const laneFilter = String(searchParams.get('lane') || '').toLowerCase() === 'connected'
    ? 'connected'
    : 'all';
  const lanesToRender = laneFilter === 'connected' ? ['connected'] : LANES;

  const toggleLaneExpanded = useCallback((lane) => {
    setExpandedLanes((prev) => {
      const next = new Set(prev);
      if (next.has(lane)) next.delete(lane);
      else next.add(lane);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      // Non-organic always fetched first with its own 500-row window — this
      // guarantees the real pipeline (prospect / invited / connected /
      // sent / booked) renders regardless of how many organic connections
      // have been swept in. Organic is split into two fetches:
      //   1. Engaged organic (connected+): always included so leads that have
      //      been messaged never disappear when the network toggle is off.
      //   2. Full organic (including prospects): only when toggle is on.
      // Fixes v3.3.17 starvation when organic lead count > 500.
      const ORGANIC_ENGAGED_STAGES = 'connected,sent,clicked,booked,warm,hot,curious,nurturing,replying,won,article_opened';
      const nonOrganicPromise = fetchLeads(api, {
        flow: 'coach',
        limit: 500,
        excludeSource: 'unipile-organic',
      });
      const organicEngagedPromise = fetchLeads(api, {
        flow: 'coach',
        limit: 500,
        source: 'unipile-organic',
        stages: ORGANIC_ENGAGED_STAGES,
      });
      const organicPromise = includeOrganicNetwork
        ? fetchLeads(api, { flow: 'coach', limit: 500, source: 'unipile-organic' })
        : Promise.resolve([]);
      const [nonOrganicData, organicEngagedData, organicData, campData, principalData, articleData] = await Promise.all([
        nonOrganicPromise,
        organicEngagedPromise,
        organicPromise,
        fetchCampaigns(api, { flow: 'coach' }).catch(() => []),
        fetchMyPrincipal(api).catch(() => null),
        fetchArticles(api).catch(() => []),
      ]);
      const nonOrganicList = Array.isArray(nonOrganicData) ? nonOrganicData : (nonOrganicData?.leads || []);
      const organicEngagedList = Array.isArray(organicEngagedData) ? organicEngagedData : (organicEngagedData?.leads || []);
      const organicList = Array.isArray(organicData) ? organicData : (organicData?.leads || []);
      const leadById = new Map();
      for (const lead of nonOrganicList) leadById.set(lead.id, lead);
      for (const lead of organicEngagedList) if (!leadById.has(lead.id)) leadById.set(lead.id, lead);
      for (const lead of organicList) if (!leadById.has(lead.id)) leadById.set(lead.id, lead);
      const leadList = Array.from(leadById.values());
      const campList = Array.isArray(campData) ? campData : (campData?.campaigns || []);
      const articleList = Array.isArray(articleData) ? articleData : (articleData?.pages || articleData?.articles || []);
      const nextPrincipal = principalData || null;
      setLeads(leadList);
      setCampaigns(campList);
      setPrincipal(nextPrincipal);
      if (nextPrincipal?.id) {
        const persisted = localStorage.getItem(includeNetworkStorageKey(nextPrincipal.id));
        setIncludeOrganicNetwork(persisted === '1');
      }
      setLaneAutomation(normalizeLaneAutomation(nextPrincipal?.laneAutomation));
      setArticles(articleList);
      if (nextPrincipal) {
        const [prospectStatus, connectedStatus] = await Promise.all([
          fetchProspectAutoStatus(api).catch(() => null),
          getConnectedAutoStatus(api).catch(() => null),
        ]);
        setProspectAutoStatus(prospectStatus);
        setConnectedAutoStatus(connectedStatus);
      } else {
        setProspectAutoStatus(null);
        setConnectedAutoStatus(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [api, includeOrganicNetwork]);

  useEffect(() => { load(); }, [load]);

  // Refetch on tab focus / visibility change so a coach who publishes an article
  // in another tab (or navigates away and back) sees fresh state without needing
  // a hard refresh. Both listeners because visibilitychange doesn't fire on
  // pure focus swaps in some browsers and focus doesn't fire on tab returns.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => {
      fetchProspectAutoStatus(api)
        .then((status) => setProspectAutoStatus(status))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(timer);
  }, [api]);

  useEffect(() => {
    if (expandedLaneConfig !== 'connected') return undefined;
    const refresh = () => {
      getConnectedAutoStatus(api)
        .then((status) => setConnectedAutoStatus(status))
        .catch(() => {});
    };
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, [api, expandedLaneConfig]);

  useEffect(() => {
    if (expandedLaneConfig !== 'connected') return;
    const nextMode = String(laneAutomation.connected || '').toLowerCase() === 'semi' ? 'semi' : 'manual';
    setConnectedModeDraft(nextMode);
    setConnectedCapDraft(Number(principal?.dailyMessageCap ?? 10));
  }, [expandedLaneConfig, laneAutomation.connected, principal?.dailyMessageCap]);

  useEffect(() => {
    if (String(laneAutomation.connected || '').toLowerCase() !== 'semi') return;
    pingConnectedAutoNextTask(api).catch(() => {});
  }, [api, laneAutomation.connected]);

  useEffect(() => {
    const timer = setInterval(() => setCountdownNowMs(Date.now()), 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!draggingLeadId) return undefined;

    function findScrollableAncestor(node) {
      let current = node;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const overflowX = style.overflowX || style.overflow;
        const overflowY = style.overflowY || style.overflow;
        const canScrollX = /(auto|scroll)/.test(overflowX) && current.scrollWidth > current.clientWidth;
        const canScrollY = /(auto|scroll)/.test(overflowY) && current.scrollHeight > current.clientHeight;
        if (canScrollX || canScrollY) return current;
        current = current.parentElement;
      }
      return null;
    }

    const scrollTarget = findScrollableAncestor(boardRef.current);
    let dx = 0;
    let dy = 0;
    let rafId = null;

    function stopLoop() {
      dx = 0;
      dy = 0;
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function step() {
      if (dx === 0 && dy === 0) {
        rafId = null;
        return;
      }
      if (scrollTarget) {
        if (dx !== 0) scrollTarget.scrollLeft += dx;
        if (dy !== 0) scrollTarget.scrollTop += dy;
      } else {
        window.scrollBy(dx, dy);
      }
      rafId = requestAnimationFrame(step);
    }

    function onDragOver(e) {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      dx = clientX < DRAG_EDGE_PX
        ? -DRAG_SCROLL_SPEED
        : (clientX > innerWidth - DRAG_EDGE_PX ? DRAG_SCROLL_SPEED : 0);
      dy = clientY < DRAG_EDGE_PX
        ? -DRAG_SCROLL_SPEED
        : (clientY > innerHeight - DRAG_EDGE_PX ? DRAG_SCROLL_SPEED : 0);
      if ((dx !== 0 || dy !== 0) && rafId == null) rafId = requestAnimationFrame(step);
      if (dx === 0 && dy === 0) stopLoop();
    }

    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragend', stopLoop);
    window.addEventListener('dragcancel', stopLoop);
    window.addEventListener('drop', stopLoop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragend', stopLoop);
      window.removeEventListener('dragcancel', stopLoop);
      window.removeEventListener('drop', stopLoop);
      stopLoop();
    };
  }, [draggingLeadId]);

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    let active = leads.filter((l) => l.archived !== true);
    if (!includeOrganicNetwork) {
      active = active.filter((lead) => !isOrganicLead(lead));
    }
    if (selectedCampaign !== 'all') {
      active = active.filter((l) => String(l.campaignId || '') === String(selectedCampaign));
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      active = active.filter((l) => {
        const name = String(l.name || '').toLowerCase();
        const company = String(l.company || '').toLowerCase();
        const title = String(l.title || '').toLowerCase();
        return name.includes(q) || company.includes(q) || title.includes(q);
      });
    }
    return active;
  }, [leads, selectedCampaign, searchQuery, includeOrganicNetwork]);

  const selectableCampaigns = useMemo(
    () => (Array.isArray(campaigns) ? campaigns : []).filter((campaign) => !isOrganicCampaign(campaign)),
    [campaigns]
  );

  const byLane = useMemo(() => {
    const map = { prospect: [], invited: [], connected: [], sent: [], booked: [] };
    for (const l of filteredLeads) {
      const lane = laneOf(l);
      if (map[lane]) map[lane].push(l);
    }
    // Sent lane: cooled cards sink, fresh-article cards rise.
    map.sent.sort((a, b) => {
      const bucketOf = (lead) => {
        const drafts = (lead.articleDrafts || []).filter((d) => d.approvedAt);
        const sentSlugs = new Set(drafts.filter((d) => d.source === 'article-share').map((d) => d.articleSlug).filter(Boolean));
        const maxApproved = drafts.reduce((m, d) => Math.max(m, d.approvedAt ? new Date(d.approvedAt).getTime() : 0), 0);
        const isCooled = sentSlugs.size > 0;
        const hasFresher = (articles || []).some((a) => a.slug && !sentSlugs.has(a.slug) && (a.createdAt ? new Date(a.createdAt).getTime() : 0) > maxApproved);
        if (isCooled && hasFresher) return 0; // ready-for-next
        if (!isCooled) return 1; // uncooled edge case
        return 2; // cooling
      };
      const ba = bucketOf(a);
      const bb = bucketOf(b);
      if (ba !== bb) return ba - bb;
      const ta = new Date(a.lastActivityAt || a.updatedAt || 0).getTime();
      const tb = new Date(b.lastActivityAt || b.updatedAt || 0).getTime();
      return tb - ta;
    });
    // Prospect: sortOrder is canonical once backfilled. Null sortOrder falls back
    // to the legacy display order (manual URL-adds first, then fitScore DESC).
    map.prospect.sort((a, b) => {
      const aOrder = typeof a.sortOrder === 'number' && Number.isFinite(a.sortOrder)
        ? a.sortOrder
        : Number.POSITIVE_INFINITY;
      const bOrder = typeof b.sortOrder === 'number' && Number.isFinite(b.sortOrder)
        ? b.sortOrder
        : Number.POSITIVE_INFINITY;
      if (aOrder !== bOrder) return aOrder - bOrder;

      const aManual = typeof a.fitScore !== 'number';
      const bManual = typeof b.fitScore !== 'number';
      if (aManual !== bManual) return aManual ? -1 : 1;
      if (aManual && bManual) {
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      }
      return b.fitScore - a.fitScore;
    });
    for (const key of ['invited', 'connected', 'booked']) {
      map[key].sort((a, b) => {
        const at = new Date(a.lastTouchAt || a.updatedAt || a.createdAt || 0).getTime();
        const bt = new Date(b.lastTouchAt || b.updatedAt || b.createdAt || 0).getTime();
        return bt - at;
      });
    }
    return map;
  }, [filteredLeads, articles]);

  const invitedFilteredLeads = useMemo(() => {
    const all = byLane.invited || [];
    const q = invitedSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((l) => String(l.name || '').toLowerCase().includes(q));
  }, [byLane.invited, invitedSearch]);

  const campaignSelectData = useMemo(() => ([
    { value: 'all', label: 'All campaigns' },
    ...selectableCampaigns.map((c) => ({ value: String(c.id), label: c.name || 'Untitled' })),
  ]), [selectableCampaigns]);

  const pausedCampaignCount = useMemo(
    () => (Array.isArray(campaigns) ? campaigns : []).filter((c) => c.paused === true && c.archived !== true).length,
    [campaigns],
  );

  const selectedCampaignObj = selectableCampaigns.find((c) => String(c.id) === String(selectedCampaign)) || null;
  const laneMode = useCallback((lane) => {
    if (lane === 'prospect') {
      const m = laneAutomation[lane];
      return m === 'auto' || m === 'semi' ? m : 'manual';
    }
    if (lane === 'connected') {
      return String(laneAutomation[lane] || '').toLowerCase() === 'semi' ? 'semi' : 'manual';
    }
    return laneAutomation[lane] === 'auto' ? 'auto' : 'manual';
  }, [laneAutomation]);

  const shareEligibleConnected = useMemo(
    () => byLane.connected.filter((l) => !hasSentArticleShareDraft(l)),
    [byLane.connected]
  );

  const failedConnectedDraftCount = useMemo(() => {
    if (!Array.isArray(leads)) return 0;
    let n = 0;
    for (const lead of leads) {
      for (const d of (lead.articleDrafts || [])) {
        const status = String(d?.status || '').toLowerCase();
        if ((status === 'failed' || d?.failedAt) && !d?.sentAt && !d?.skippedAt) n += 1;
      }
    }
    return n;
  }, [leads]);

  const laneCounts = {
    prospect: byLane.prospect.length,
    invited: byLane.invited.length,
    connected: byLane.connected.length,
    sent: byLane.sent.length,
    booked: byLane.booked.length,
  };

  const nextSendCountdown = useMemo(() => {
    const nextLeadId = String(prospectAutoStatus?.nextLeadId || '');
    if (!nextLeadId) return { leadId: null, label: null };
    const timezone = principal?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const label = formatNextSendCountdown(prospectAutoStatus?.nextSendAt, countdownNowMs, timezone);
    if (!label) return { leadId: null, label: null };
    return { leadId: nextLeadId, label };
  }, [prospectAutoStatus?.nextLeadId, prospectAutoStatus?.nextSendAt, principal?.timezone, countdownNowMs]);

  const handleProspectSortEnd = useCallback(async (event) => {
    setDraggingLeadId(null);
    setHoverLane(null);
    const activeId = String(event?.active?.id || '');
    const overId = String(event?.over?.id || '');
    if (!activeId || !overId || activeId === overId) return;

    const visibleProspects = [...(byLane.prospect || [])];
    const oldIndex = visibleProspects.findIndex((lead) => String(lead.id) === activeId);
    const newIndex = visibleProspects.findIndex((lead) => String(lead.id) === overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

    const reordered = arrayMove(visibleProspects, oldIndex, newIndex);
    const movedLead = reordered.find((lead) => String(lead.id) === activeId);
    if (!movedLead) return;

    const nextSortOrder = computeProspectDropSortOrder(reordered, activeId, movedLead.sortOrder);
    if (!Number.isFinite(nextSortOrder) || nextSortOrder === movedLead.sortOrder) return;

    const previousSnapshot = leads;
    setLeads((prev) => (prev || []).map((lead) => (
      String(lead.id) === activeId
        ? { ...lead, sortOrder: nextSortOrder, updatedAt: new Date().toISOString() }
        : lead
    )));

    try {
      await updateLead(api, activeId, { sortOrder: nextSortOrder });
    } catch (err) {
      setLeads(previousSnapshot);
      notifications.show({
        title: 'Could not save new order',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    }
  }, [api, byLane.prospect, leads]);

  // ─── Actions ──────────────────────────────────────────────────────────────
  const linkedinConnected = useMemo(() => {
    const accounts = Array.isArray(principal?.unipileAccounts) ? principal.unipileAccounts : [];
    return accounts.some((a) => String(a?.channel || '').toLowerCase() === 'linkedin' && a?.accountId);
  }, [principal]);

  function isLaneLocked(lane) {
    if (LOCKED_LANES.has(lane)) return true;
    // Invited auto needs a live Unipile LinkedIn subscription to fire the webhook.
    if (lane === 'invited' && !linkedinConnected) return true;
    return false;
  }

  function lockReason(lane) {
    if (lane === 'invited' && !linkedinConnected) {
      return 'Connect LinkedIn in Settings first — webhook needs an account to listen on';
    }
    return null;
  }

  async function handleLaneModeChange(lane, nextMode) {
    if (!lane || isLaneLocked(lane)) return;
    if (lane === 'prospect') {
      if (!['manual', 'semi', 'auto'].includes(nextMode)) return;
    } else if (lane === 'connected') {
      if (!['manual', 'semi'].includes(nextMode)) return;
    } else if (nextMode !== 'auto' && nextMode !== 'manual') return;
    const previous = laneMode(lane);
    if (previous === nextMode || updatingLane) return;
    setUpdatingLane(lane);
    setLaneAutomation((prev) => ({ ...prev, [lane]: nextMode }));
    try {
      const updatedPrincipal = await updateMyPrincipal(api, {
        laneAutomation: { [lane]: nextMode },
      });
      const normalized = normalizeLaneAutomation(updatedPrincipal?.laneAutomation);
      setPrincipal(updatedPrincipal || principal);
      setLaneAutomation(normalized);
      if (lane === 'prospect') {
        const status = await fetchProspectAutoStatus(api).catch(() => null);
        setProspectAutoStatus(status);
      }
      if (lane === 'connected') {
        const status = await getConnectedAutoStatus(api).catch(() => null);
        setConnectedAutoStatus(status);
      }
    } catch (err) {
      setLaneAutomation((prev) => ({ ...prev, [lane]: previous }));
      notifications.show({
        title: 'Could not update lane mode',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setUpdatingLane(null);
    }
  }

  async function handleResumeConnectedSending() {
    if (resettingConnectedFailures) return;
    setResettingConnectedFailures(true);
    try {
      const status = await resetConnectedAutoFailures(api);
      setConnectedAutoStatus(status);
      const draftsReset = Number(status?.draftsReset || 0);
      notifications.show({
        title: 'Connected sending resumed',
        message: draftsReset > 0
          ? `${draftsReset} failed draft${draftsReset === 1 ? '' : 's'} re-queued. Extension will pick up the next task on its next poll.`
          : 'Extension will pick up its next task on the next poll (~30s).',
        color: 'sage',
      });
      // Refresh so the popover's failed-count and lane pills clear in place.
      load();
    } catch (err) {
      notifications.show({
        title: 'Could not resume',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setResettingConnectedFailures(false);
    }
  }

  async function handleSaveConnectedSettings() {
    if (savingConnectedSettings) return;
    const nextMode = connectedModeDraft === 'semi' ? 'semi' : 'manual';
    const nextCap = Math.max(1, Math.min(50, Math.round(Number(connectedCapDraft) || 10)));
    const wasSemi = laneMode('connected') === 'semi';
    const needsConsent = !wasSemi && nextMode === 'semi' && !principal?.connectedAutoConsentAt;
    setSavingConnectedSettings(true);
    try {
      if (needsConsent) {
        await postConnectedAutoConsent(api);
      }
      const updatedPrincipal = await updateMyPrincipal(api, {
        laneAutomation: { connected: nextMode },
        dailyMessageCap: nextCap,
      });
      const normalized = normalizeLaneAutomation(updatedPrincipal?.laneAutomation);
      setPrincipal(updatedPrincipal || principal);
      setLaneAutomation(normalized);
      const status = await getConnectedAutoStatus(api).catch(() => null);
      setConnectedAutoStatus(status);
      notifications.show({
        title: 'Connected lane settings saved',
        message: nextMode === 'semi' ? 'Semi mode is enabled for Connected.' : 'Connected automation is off.',
        color: 'sage',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not save connected settings',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSavingConnectedSettings(false);
    }
  }

  async function handleDrop(leadId, targetLane) {
    const lead = (leads || []).find((l) => String(l.id) === String(leadId));
    if (!lead) return;
    const currentLane = laneOf(lead);
    if (currentLane === targetLane) return;
    const patch = laneToPatch(targetLane);
    if (!patch) return;

    const prevSnapshot = leads;
    setLeads((prev) => (prev || []).map((l) => (
      String(l.id) === String(leadId) ? { ...l, ...patch } : l
    )));

    try {
      await updateLead(api, leadId, patch);
      notifications.show({
        title: `Moved to ${LANE_TITLES[targetLane]}`,
        message: `${lead.name || 'Prospect'} · stage updated`,
        color: 'sage',
      });
    } catch (err) {
      setLeads(prevSnapshot);
      notifications.show({
        title: 'Could not move card',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    }
  }

  function handleProspectAdded(lead) {
    setLeads((prev) => (prev ? [lead, ...prev] : [lead]));
    load();
  }

  const openShareQueue = useCallback((articleSlug = null) => {
    if (!shareEligibleConnected.length) return;
    const firstLead = shareEligibleConnected[0];
    const campaignParam = selectedCampaign && selectedCampaign !== 'all' ? String(selectedCampaign) : 'all';
    let url = `/v2/leads/${encodeURIComponent(firstLead.id)}?queue=connected&campaign=${encodeURIComponent(campaignParam)}`;
    if (articleSlug) url += `&articleSlug=${encodeURIComponent(articleSlug)}`;
    navigate(url);
  }, [shareEligibleConnected, navigate, selectedCampaign]);

  const openNewCampaign = useCallback(() => {
    navigate('/v2?new=1');
  }, [navigate]);

  const openRefreshCampaign = useCallback((campaign) => {
    const target = campaign || selectedCampaignObj;
    if (!target) return;
    navigate(`/v2?mode=edit&campaignId=${encodeURIComponent(target.id)}`);
  }, [navigate, selectedCampaignObj]);

  const openNewArticle = useCallback(() => {
    navigate('/v2/articles/new');
  }, [navigate]);

  const openShareArticle = useCallback((article) => {
    if (!article) return;
    openShareQueue(article.slug);
  }, [openShareQueue]);

  // Card click routes by lane:
  //   Connected → lead detail queue mode (article-share flow + queue walk)
  //   Everything else → lead detail (opener flow / read-only view)
  const openLead = useCallback((lead, lane) => {
    const leadId = lead?.id;
    if (!leadId) return;
    if (lane === 'connected') {
      const campaignParam = selectedCampaign && selectedCampaign !== 'all' ? String(selectedCampaign) : 'all';
      navigate(`/v2/leads/${encodeURIComponent(leadId)}?queue=connected&campaign=${encodeURIComponent(campaignParam)}`);
      return;
    }
    if (lane === 'sent') {
      const campaignParam = String(lead?.campaignId || 'all');
      navigate(`/v2/leads/${encodeURIComponent(leadId)}?queue=connected&campaign=${encodeURIComponent(campaignParam)}`);
      return;
    }
    navigate(`/v2/leads/${leadId}`, { state: { back: { path: BOARD_PATH, label: 'Board' } } });
  }, [navigate, selectedCampaign]);

  // Lane header click → per-lane workflow.
  //   Prospect  → walk through Prospect leads one-by-one (existing lead detail
  //               page has prev/next stepper via nextUnapproved).
  //   Connected → open share queue (same as Convert.Connected pill).
  //   Others    → placeholder: campaigns list until we have per-lane workflows.
  const openLaneList = useCallback((lane) => {
    if (lane === 'prospect') {
      const first = byLane.prospect[0];
      if (first) {
        navigate(`/v2/leads/${first.id}?tab=message`, { state: { back: { path: BOARD_PATH, label: 'Board' } } });
        return;
      }
    }
    if (lane === 'connected') {
      if (shareEligibleConnected.length > 0) {
        openShareQueue();
        return;
      }
    }
    navigate('/v2/campaigns');
  }, [byLane.prospect, shareEligibleConnected, openShareQueue, navigate]);

  // Iteration 20: quiet caption per lane. Amber badge shown only when a
  // manual lane has actual work waiting (count > 0). Booked → tiny 🎉 caption.
  const captionFor = (lane) => {
    if (lane === 'booked') {
      return { mode: 'auto', text: '🎉 signed up', workBadge: null };
    }
    if (lane === 'prospect') {
      if (laneMode('prospect') === 'semi') {
        const cap = Number(prospectAutoStatus?.cap || 20);
        const sentToday = Number(prospectAutoStatus?.sentToday || 0);
        if (!prospectAutoStatus?.extensionConnected) {
          return {
            mode: 'manual',
            text: 'waiting for Chrome extension · install from Settings',
            workBadge: `${cap}/day when ready`,
          };
        }
        if (prospectAutoStatus?.handoutGateOk) {
          return {
            mode: 'auto',
            text: `extension ready · sent ${sentToday}/${cap} today`,
            workBadge: null,
          };
        }
        return {
          mode: 'manual',
          text: `extension paused — ${extensionGateLabel(prospectAutoStatus?.handoutReason)}`,
          workBadge: null,
        };
      }
      if (laneMode('prospect') === 'auto') {
        const cap = Number(prospectAutoStatus?.cap || 20);
        const sentToday = Number(prospectAutoStatus?.sentToday || 0);
        if (prospectAutoStatus?.paused) {
          return {
            mode: 'manual',
            text: `paused — ${prospectAutoStatus?.pauseReason || 'circuit-breaker'}`,
            workBadge: null,
          };
        }
        const nextText = prospectAutoStatus?.nextTimeLabel ? ` · next ${prospectAutoStatus.nextTimeLabel}` : '';
        return {
          mode: 'auto',
          text: `sent ${sentToday}/${cap} today${nextText}`,
          workBadge: null,
        };
      }
      return {
        mode: 'manual',
        text: 'lane waits for you',
        workBadge: laneCounts.prospect > 0 ? `${laneCounts.prospect} to invite` : null,
      };
    }
    if (lane === 'invited') {
      return laneMode('invited') === 'auto'
        ? { mode: 'auto', text: 'accepted invites move on their own', workBadge: null }
        : {
            mode: 'manual',
            text: 'you decide when each invite moves',
            workBadge: laneCounts.invited > 0 ? `${laneCounts.invited} to check` : null,
          };
    }
    if (lane === 'connected') {
      if (laneMode('connected') === 'semi') {
        const cap = Number(connectedAutoStatus?.cap || principal?.dailyMessageCap || 10);
        const sentToday = Number(connectedAutoStatus?.sentToday || 0);
        if (!connectedAutoStatus?.extensionConnected) {
          return {
            mode: 'manual',
            text: 'waiting for Chrome extension · install from Settings',
            workBadge: `${cap}/day when ready`,
          };
        }
        if (connectedAutoStatus?.handoutGateOk) {
          return {
            mode: 'auto',
            text: `extension ready · sent ${sentToday}/${cap} today`,
            workBadge: null,
          };
        }
        // Circuit-breaker paused: show Resume button so coach can self-serve.
        // Any other pause reason (business hours, daily cap, etc.) is time-based
        // — the coach can't override, so no button.
        const isCircuitPaused = connectedAutoStatus?.handoutReason === 'circuit-paused'
          || Boolean(connectedAutoStatus?.circuitBrokenAt);
        return {
          mode: 'manual',
          text: `extension paused — ${extensionGateLabel(connectedAutoStatus?.handoutReason)}`,
          workBadge: null,
          actionButton: isCircuitPaused
            ? {
              label: 'Resume',
              onClick: handleResumeConnectedSending,
              busy: resettingConnectedFailures,
            }
            : null,
        };
      }
      return {
        mode: 'manual',
        text: 'lane waits for you',
        workBadge: shareEligibleConnected.length > 0
          ? `${shareEligibleConnected.length} ready to share`
          : null,
      };
    }
    if (lane === 'sent') {
      return laneMode('sent') === 'auto'
        ? { mode: 'auto', text: 'watching for clicks', workBadge: null }
        : {
            mode: 'manual',
            text: 'you pick when each reader moves',
            workBadge: laneCounts.sent > 0 ? `${laneCounts.sent} to check` : null,
          };
    }
    return null;
  };

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn&apos;t load the pipeline</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }
  if (leads === null) return <Center py={80}><Loader color="sage" /></Center>;

  return (
    <Box ref={boardRef} style={{ padding: '24px 20px 120px' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Group gap="md" align="center">
            <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>Pipeline</Text>
            <ExtensionStatusPill status={prospectAutoStatus} />
            {laneFilter === 'connected' && (
              <UnstyledButton
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('lane');
                  setSearchParams(next);
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--mantine-color-sage-4)',
                  background: 'var(--mantine-color-sage-0)',
                  color: 'var(--mantine-color-sage-8)',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Connected lane filter active - clear
              </UnstyledButton>
            )}
          </Group>
          <Group gap="sm">
            <TextInput
              placeholder="Search name, company, title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              styles={{ input: { width: 240 } }}
            />
            <Select
              value={selectedCampaign}
              onChange={(v) => setSelectedCampaign(v || 'all')}
              data={campaignSelectData}
              allowDeselect={false}
              withCheckIcon={false}
              styles={{ input: { width: 220 } }}
            />
            {pausedCampaignCount > 0 && (
              <UnstyledButton
                onClick={() => navigate('/v2/campaigns')}
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--mantine-color-orange-4)',
                  background: 'var(--mantine-color-orange-0)',
                  color: 'var(--mantine-color-orange-8)',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                ⏸ {pausedCampaignCount} paused
              </UnstyledButton>
            )}
            <Button
              size="xs"
              color={includeOrganicNetwork ? 'sage' : 'gray'}
              variant={includeOrganicNetwork ? 'filled' : 'default'}
              onClick={() => {
                const next = !includeOrganicNetwork;
                setIncludeOrganicNetwork(next);
                if (principal?.id) {
                  localStorage.setItem(includeNetworkStorageKey(principal.id), next ? '1' : '0');
                }
              }}
            >
              Include LinkedIn network
            </Button>
          </Group>
        </Group>

        <Group align="stretch" wrap="nowrap" gap="md" style={{ paddingBottom: 8 }}>
          {lanesToRender.map((lane) => {
            const laneLeads = lane === 'invited' ? invitedFilteredLeads : byLane[lane];
            const isExpanded = expandedLanes.has(lane);
            const visible = isExpanded ? laneLeads : laneLeads.slice(0, MAX_VISIBLE_PER_LANE);
            const hidden = Math.max(0, laneLeads.length - visible.length);
            const caption = captionFor(lane);

            return (
              <Stack
                key={lane}
                gap="xs"
                onDragOver={(e) => {
                  if (!draggingLeadId) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (hoverLane !== lane) setHoverLane(lane);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setHoverLane((prev) => (prev === lane ? null : prev));
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const leadId = e.dataTransfer.getData('text/plain');
                  setHoverLane(null);
                  if (leadId) handleDrop(leadId, lane);
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: 10,
                  borderRadius: 12,
                  background: hoverLane === lane ? 'var(--mantine-color-sage-0)' : 'transparent',
                  outline: hoverLane === lane ? '2px dashed var(--mantine-color-sage-4)' : 'none',
                  outlineOffset: hoverLane === lane ? -4 : 0,
                  transition: 'background 120ms ease',
                }}
              >
                <Group justify="space-between" align="center" wrap="nowrap" style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: '#fff',
                  border: '1px solid var(--aviary-disabled)',
                }}>
                  <UnstyledButton
                    onClick={() => openLaneList(lane)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Group gap={6} align="baseline">
                        <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>{LANE_TITLES[lane]}</Text>
                        <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>{laneCounts[lane]}</Text>
                      </Group>
                      <IconArrowUpRight size={12} style={{ color: 'var(--aviary-muted)' }} />
                    </Group>
                  </UnstyledButton>
                  <Tooltip
                    disabled={!isLaneLocked(lane)}
                    label={lockReason(lane) || ''}
                    withArrow
                  >
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => setExpandedLaneConfig((prev) => (prev === lane ? null : lane))}
                      aria-label={`${LANE_TITLES[lane]} lane settings`}
                      style={{ color: expandedLaneConfig === lane ? 'var(--aviary-ink)' : 'var(--aviary-muted)' }}
                    >
                      <IconSettings size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>

                {expandedLaneConfig === lane && (
                  <Paper
                    withBorder
                    radius="md"
                    p="sm"
                    style={{
                      marginTop: 8,
                      background: '#fff',
                      borderColor: 'var(--aviary-disabled)',
                    }}
                  >
                    {lane === 'prospect' ? (
                      <Stack gap="xs">
                        <Group justify="space-between" align="center">
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>Automation</Text>
                          <SegmentedControl
                            size="xs"
                            value={laneMode('prospect')}
                            onChange={(next) => handleLaneModeChange('prospect', next)}
                            data={[
                              { value: 'manual', label: 'off' },
                              { value: 'semi', label: 'semi' },
                              { value: 'auto', label: 'full' },
                            ]}
                            disabled={updatingLane === 'prospect'}
                            styles={{
                              root: { background: 'var(--aviary-disabled)', border: 'none' },
                              label: { color: 'var(--aviary-muted)', fontWeight: 500 },
                              labelActive: { color: 'var(--aviary-ink) !important', fontWeight: 600 },
                              indicator: { background: '#fff', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)' },
                            }}
                          />
                        </Group>
                        <Group justify="space-between" align="center">
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>Throttle</Text>
                          <NumberInput
                            size="xs"
                            value={Number(principal?.dailyInviteCap ?? 20)}
                            onChange={async (v) => {
                              const next = Math.max(1, Math.min(100, Math.round(Number(v) || 20)));
                              setPrincipal((prev) => (prev ? { ...prev, dailyInviteCap: next } : prev));
                              setSavingThrottle(true);
                              try {
                                const updated = await updateMyPrincipal(api, { dailyInviteCap: next });
                                setPrincipal(updated || principal);
                              } catch (err) {
                                notifications.show({
                                  title: 'Could not save throttle',
                                  message: err.response?.data?.error || err.message,
                                  color: 'red',
                                });
                              } finally {
                                setSavingThrottle(false);
                              }
                            }}
                            min={1}
                            max={100}
                            suffix=" /day"
                            hideControls
                            disabled={savingThrottle}
                            styles={{ input: { textAlign: 'right', width: 90 } }}
                          />
                        </Group>
                      </Stack>
                    ) : lane === 'connected' ? (
                      <Stack gap="xs">
                        <Group justify="space-between" align="center">
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>Automation</Text>
                          <SegmentedControl
                            size="xs"
                            value={connectedModeDraft}
                            onChange={setConnectedModeDraft}
                            data={[
                              { value: 'manual', label: 'off' },
                              { value: 'semi', label: 'semi' },
                            ]}
                            disabled={savingConnectedSettings}
                            styles={{
                              root: { background: 'var(--aviary-disabled)', border: 'none' },
                              label: { color: 'var(--aviary-muted)', fontWeight: 500 },
                              labelActive: { color: 'var(--aviary-ink) !important', fontWeight: 600 },
                              indicator: { background: '#fff', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)' },
                            }}
                          />
                        </Group>
                        <Group justify="space-between" align="center">
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>Throttle</Text>
                          <NumberInput
                            size="xs"
                            value={Number(connectedCapDraft || 10)}
                            onChange={(v) => setConnectedCapDraft(Math.max(1, Math.min(50, Math.round(Number(v) || 10))))}
                            min={1}
                            max={50}
                            suffix=" /day"
                            hideControls
                            disabled={savingConnectedSettings}
                            styles={{ input: { textAlign: 'right', width: 90 } }}
                          />
                        </Group>
                        <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                          {connectedAutoStatus?.extensionConnected ? 'extension connected' : 'extension not connected'}
                          {' · '}
                          sent {Number(connectedAutoStatus?.sentToday || 0)}/{Number(connectedAutoStatus?.cap || principal?.dailyMessageCap || 10)} today
                          {connectedAutoStatus?.handoutGateOk === false
                            ? ` · ${extensionGateLabel(connectedAutoStatus?.handoutReason)}`
                            : ''}
                          {connectedAutoStatus?.nextTimeLabel
                            ? ` · next ${connectedAutoStatus.nextTimeLabel}`
                            : ''}
                        </Text>
                        {connectedModeDraft === 'semi' && !principal?.connectedAutoConsentAt && (
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                            By enabling semi mode, you agree Aviary can prepare messages on your behalf for approval.
                          </Text>
                        )}
                        {(failedConnectedDraftCount > 0 || connectedAutoStatus?.handoutReason === 'circuit-paused' || connectedAutoStatus?.circuitBrokenAt) && (
                          <Group justify="space-between" align="center">
                            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                              {failedConnectedDraftCount > 0
                                ? `${failedConnectedDraftCount} send${failedConnectedDraftCount === 1 ? '' : 's'} failed`
                                : 'Extension paused after failures'}
                            </Text>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              onClick={handleResumeConnectedSending}
                              loading={resettingConnectedFailures}
                            >
                              Reset failed sends
                            </Button>
                          </Group>
                        )}
                        <Group justify="flex-end">
                          <Button
                            size="xs"
                            color="sage"
                            onClick={handleSaveConnectedSettings}
                            loading={savingConnectedSettings}
                          >
                            Save
                          </Button>
                        </Group>
                      </Stack>
                    ) : lane === 'booked' ? (
                      <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                        Finish line — no automation options here yet.
                      </Text>
                    ) : (
                      <Stack gap="xs">
                        <Group justify="space-between" align="center">
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>Automation</Text>
                          <SegmentedControl
                            size="xs"
                            value={laneMode(lane)}
                            onChange={(next) => handleLaneModeChange(lane, next)}
                            data={[
                              { value: 'manual', label: 'off' },
                              { value: 'auto', label: 'on' },
                            ]}
                            disabled={isLaneLocked(lane) || updatingLane === lane}
                            styles={{
                              root: { background: 'var(--aviary-disabled)', border: 'none' },
                              label: { color: 'var(--aviary-muted)', fontWeight: 500 },
                              labelActive: { color: 'var(--aviary-ink) !important', fontWeight: 600 },
                              indicator: { background: '#fff', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)' },
                            }}
                          />
                        </Group>
                      </Stack>
                    )}
                  </Paper>
                )}

                {caption && (
                  <LaneCaption
                    mode={caption.mode}
                    text={caption.text}
                    workBadge={caption.workBadge}
                    actionButton={caption.actionButton}
                  />
                )}
                {lane === 'invited' && (
                  <InvitedSearch
                    value={invitedSearch}
                    onChange={setInvitedSearch}
                    totalCount={laneCounts.invited}
                    matchedCount={invitedFilteredLeads.length}
                  />
                )}

                {lane === 'prospect' && (
                  <ProspectTopUpCard
                    campaigns={selectableCampaigns}
                    onNew={openNewCampaign}
                    onRefresh={openRefreshCampaign}
                  />
                )}
                {lane === 'connected' && (
                  <ConnectedTopUpCard
                    connectedCount={shareEligibleConnected.length}
                    articles={articles}
                    onNewArticle={openNewArticle}
                    onShareArticle={openShareArticle}
                    onShareWithAll={() => openShareQueue()}
                  />
                )}

                <Stack gap="xs">
                  {lane === 'prospect' ? (
                    <DndContext
                      sensors={prospectDndSensors}
                      collisionDetection={closestCorners}
                      onDragStart={({ active }) => setDraggingLeadId(String(active?.id || ''))}
                      onDragCancel={() => {
                        setDraggingLeadId(null);
                        setHoverLane(null);
                      }}
                      onDragEnd={handleProspectSortEnd}
                    >
                      <SortableContext
                        items={visible.map((lead) => String(lead.id))}
                        strategy={verticalListSortingStrategy}
                      >
                        <Stack gap="xs">
                          {visible.map((lead) => (
                            <SortableProspectCard
                              key={lead.id}
                              lead={lead}
                              articles={articles}
                              nextSendCountdown={String(lead.id) === String(nextSendCountdown.leadId) ? nextSendCountdown.label : null}
                              onClick={() => openLead(lead, lane)}
                              onNativeDragStart={setDraggingLeadId}
                              onNativeDragEnd={() => { setDraggingLeadId(null); setHoverLane(null); }}
                            />
                          ))}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    visible.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        lane={lane}
                        articles={articles}
                        isDragging={draggingLeadId === lead.id}
                        onDragStart={setDraggingLeadId}
                        onDragEnd={() => { setDraggingLeadId(null); setHoverLane(null); }}
                        onClick={() => openLead(lead, lane)}
                      />
                    ))
                  )}
                  {laneLeads.length === 0 && (
                    <Box style={{
                      padding: 20, borderRadius: 10,
                      border: '1px dashed var(--aviary-disabled)', textAlign: 'center',
                    }}>
                      <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>empty</Text>
                    </Box>
                  )}
                  {hidden > 0 && (
                    <UnstyledButton
                      onClick={() => toggleLaneExpanded(lane)}
                      style={{
                        display: 'block', padding: 12, borderRadius: 10,
                        border: '1px dashed var(--aviary-disabled)', textAlign: 'center',
                      }}
                    >
                      <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                        +{hidden} more
                      </Text>
                    </UnstyledButton>
                  )}
                  {isExpanded && laneLeads.length > MAX_VISIBLE_PER_LANE && (
                    <UnstyledButton
                      onClick={() => toggleLaneExpanded(lane)}
                      style={{
                        display: 'block', padding: 8, borderRadius: 10, textAlign: 'center',
                      }}
                    >
                      <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic' }}>
                        ↑ show less
                      </Text>
                    </UnstyledButton>
                  )}

                  {lane === 'prospect' && (
                    <AddProspectTile
                      selectedCampaignId={selectedCampaign}
                      campaigns={selectableCampaigns}
                      onAdded={handleProspectAdded}
                    />
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Group>
      </Stack>
    </Box>
  );
}
